import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Avatar, Card } from '@orion/ui';
import type { DashboardResponse, DeltaMetric, MoneyDeltaMetric } from '@orion/contracts';
import { PageHeader } from '../../app/shell.js';
import { useAuth } from '../../lib/auth.js';
import { ApiError } from '../../lib/api-client.js';
import { useDashboardQuery } from './api.js';

/** docs section 7 "Dashboard" — real KPI, pipeline and activity queries
 * (apps/api/src/modules/dashboard), including prior-period deltas, the
 * weighted-forecast/avg-deal-size/avg-days-to-close strip and a per-rep
 * Team performance table — all real aggregations, not fabricated (see the
 * doc comment on DashboardService for how each prior-period figure is
 * reconstructed from data this app already writes: AuditLog for lead
 * status history, OpportunityStageHistory for pipeline value). */
const PERIODS = [7, 30, 90] as const;

export function DashboardPage() {
  const { session, hasPermission } = useAuth();
  const [scope, setScope] = useState<'me' | 'team'>('team');
  const [days, setDays] = useState<number>(30);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [customiseOpen, setCustomiseOpen] = useState(false);
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('orion-dashboard-hidden') ?? '[]');
    } catch {
      return [];
    }
  });
  const canView = hasPermission('reports.dashboards.view');
  const { data, isLoading, isError, error } = useDashboardQuery(scope, days, canView);
  const firstName = session?.user.displayName.split(' ')[0] ?? '';
  const noRolesAssigned = (session?.permissions.length ?? 0) === 0;

  const toggleWidget = (key: string) => {
    setHidden((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem('orion-dashboard-hidden', JSON.stringify(next));
      } catch {
        // Per-device convenience only.
      }
      return next;
    });
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <>
      <PageHeader title="Dashboard" />
      <main className="flex flex-1 flex-col gap-3.5 overflow-hidden px-7 py-5">
        <div className="flex shrink-0 items-end justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="font-sans text-[32px] font-normal leading-9 tracking-[-0.01em]">Good morning, {firstName}</h1>
            {data && (
              <p className="text-sm text-text-muted">
                {today} · You have{' '}
                <Link to="/tasks" className="font-semibold text-ink hover:underline">
                  {data.tasksDueToday} tasks due
                </Link>{' '}
                and{' '}
                <Link to="/tasks" className="font-semibold text-danger hover:underline">
                  {data.tasksOverdue} overdue
                </Link>
                .
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div className="flex h-9 items-center gap-0.5 rounded-control border border-border-strong bg-surface p-0.75 text-sm font-medium">
              {(['team', 'me'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={
                    scope === s
                      ? 'flex h-full items-center rounded-[6px] bg-primary px-3 text-white'
                      : 'flex h-full items-center rounded-[6px] px-3 text-text-secondary hover:bg-surface-subtle'
                  }
                >
                  {s === 'me' ? 'Just me' : 'My team'}
                </button>
              ))}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setPeriodOpen((o) => !o)}
                className="flex h-9 items-center gap-2 rounded-control border border-border-strong bg-surface px-3 text-sm font-medium text-ink hover:bg-surface-subtle"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M16 3v4M8 3v4M3 10h18" />
                </svg>
                Last {days} days
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              {periodOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setPeriodOpen(false)} />
                  <div className="absolute right-0 top-10 z-50 w-36 overflow-hidden rounded-dialog border border-border bg-surface py-1 shadow-dialog">
                    {PERIODS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          setDays(p);
                          setPeriodOpen(false);
                        }}
                        className="block w-full px-3.5 py-2 text-left text-sm text-ink hover:bg-surface-subtle"
                      >
                        Last {p} days
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                aria-label="Customise dashboard"
                onClick={() => setCustomiseOpen((o) => !o)}
                className="flex size-9 items-center justify-center rounded-control border border-border-strong bg-surface text-text-secondary hover:bg-surface-subtle"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" />
                  <circle cx="16" cy="6" r="2" />
                  <circle cx="10" cy="12" r="2" />
                  <circle cx="18" cy="18" r="2" />
                </svg>
              </button>
              {customiseOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setCustomiseOpen(false)} />
                  <div className="absolute right-0 top-10 z-50 w-56 overflow-hidden rounded-dialog border border-border bg-surface py-1.5 shadow-dialog">
                    <p className="px-3.5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Show widgets</p>
                    {WIDGETS.map((w) => (
                      <label key={w.key} className="flex items-center gap-2.5 px-3.5 py-1.5 text-sm text-ink hover:bg-surface-subtle">
                        <input type="checkbox" className="size-4 accent-primary" checked={!hidden.includes(w.key)} onChange={() => toggleWidget(w.key)} />
                        {w.label}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {!canView && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {noRolesAssigned
              ? "You don't have any roles assigned yet, so there's nothing to show here. Ask your workspace admin to assign you a role in Admin → Users."
              : "You don't have permission to view the dashboard. Ask your workspace admin for access."}
          </div>
        )}

        {canView && isError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view the dashboard. Ask your workspace admin for access."
              : "Couldn't load the dashboard. Please try again."}
          </div>
        )}

        {canView && isLoading && <p className="text-sm text-text-muted">Loading…</p>}

        {data && <DashboardBody data={data} hidden={hidden} days={days} />}
      </main>
    </>
  );
}

const WIDGETS = [
  { key: 'pipeline', label: 'Pipeline by stage' },
  { key: 'sources', label: 'Lead sources' },
  { key: 'attention', label: 'Needs your attention' },
  { key: 'activity', label: 'Recent activity' },
  { key: 'team', label: 'Team performance' },
] as const;

function DashboardBody({ data, hidden, days }: { data: DashboardResponse; hidden: string[]; days: number }) {
  const show = (key: string) => !hidden.includes(key);
  return (
    <>
      <section aria-label="Key metrics" className="grid shrink-0 grid-cols-6 gap-3">
        <Kpi label="Total leads" value={data.totalLeads.value}>
          <DeltaLine metric={data.totalLeads} mode="pct" days={days} />
        </Kpi>
        <Kpi label="New leads" value={data.newLeads}>
          <Link to="/leads" className="text-xs font-semibold text-amber-text hover:underline">
            {data.newLeadsUnassigned} unassigned →
          </Link>
        </Kpi>
        <Kpi label="Qualified" value={data.qualifiedLeads.value}>
          <DeltaLine metric={data.qualifiedLeads} mode="count" days={days} />
        </Kpi>
        <Kpi label="Open opportunities" value={<>{data.openDeals} <span className="text-[15px] font-semibold text-text-secondary">· {moneyCompact(data.openPipelineValue.value)}</span></>}>
          <DeltaLine metric={data.openPipelineValue} mode="pct" suffix="pipeline value" days={days} />
        </Kpi>
        <Kpi label="Won" value={moneyCompact(data.wonValue)}>
          <span className="text-xs font-medium text-text-secondary">
            {data.wonDeals} won · {data.lostDeals} lost
          </span>
        </Kpi>
        <Kpi label="Conversion" value={`${data.conversionRate.value}%`}>
          <DeltaLine metric={data.conversionRate} mode="pts" days={days} />
        </Kpi>
      </section>

      <div className="grid min-h-0 flex-1 grid-cols-[2fr_1fr] gap-4">
        <div className="flex min-h-0 flex-col gap-3">
          {show('pipeline') && (
          <Card className="flex shrink-0 flex-col gap-4 py-4 px-5">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-0.5">
                <h3 className="text-[15px] font-semibold text-ink">Pipeline by stage</h3>
                <span className="text-[13px] text-text-muted">
                  Sales pipeline · {data.pipelineByStage.reduce((n, s) => n + s.count, 0)} open opportunities ·{' '}
                  {money(String(data.pipelineByStage.reduce((n, s) => n + Number(s.value), 0)))}
                </span>
              </div>
              <Link to="/deals" className="flex items-center gap-1 text-[13px] font-semibold text-primary-text hover:underline">
                Open pipeline
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </div>
            {data.pipelineByStage.length === 0 && <p className="text-sm text-text-muted">No open opportunities yet.</p>}
            <div role="list" className="flex flex-col gap-3">
              {data.pipelineByStage.map((s, i) => {
                const max = Math.max(...data.pipelineByStage.map((x) => Number(x.value)), 1);
                const pct = Math.max((Number(s.value) / max) * 100, 4);
                return (
                  <div key={s.stageName} role="listitem" className="grid h-7 grid-cols-[104px_1fr_80px_64px] items-center gap-3.5">
                    <span className="truncate text-[13px] font-semibold text-ink">{s.stageName}</span>
                    <div className="flex h-5 rounded-[6px] bg-ground">
                      <div className="rounded-[6px]" style={{ width: `${pct}%`, backgroundColor: stageBarColor(i, data.pipelineByStage.length) }} />
                    </div>
                    <span className="text-right text-sm font-[650] tabular-nums text-ink">{money(s.value)}</span>
                    <span className="text-right text-xs text-text-muted">{s.count} opportunities</span>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-3 rounded-lg border border-divider bg-ground">
              <ForecastStat label="Weighted forecast" value={money(data.weightedForecast)} />
              <ForecastStat label="Avg. opportunity size" value={money(data.avgDealSize)} bordered />
              <ForecastStat label="Avg. time to close" value={data.avgDaysToClose !== null ? `${data.avgDaysToClose} days` : '—'} bordered />
            </div>
          </Card>
          )}

          {show('sources') && (
          <Card className="flex min-h-0 flex-1 flex-col gap-3 py-4 px-5">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-0.5">
                <h3 className="text-[15px] font-semibold text-ink">Lead sources</h3>
                <span className="text-[13px] text-text-muted">{data.totalLeads.value} leads</span>
              </div>
              <Link to="/reports" className="text-[13px] font-semibold text-primary-text hover:underline">
                View report
              </Link>
            </div>
            {data.leadsBySource.length === 0 && <p className="text-sm text-text-muted">No leads yet.</p>}
            <div className="flex flex-col overflow-y-auto">
              <div className="grid h-6 grid-cols-[120px_1fr_44px_64px] items-center gap-3.5 border-b border-divider text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                <span>Source</span>
                <span>Share</span>
                <span className="text-right">%</span>
                <span className="text-right">Leads</span>
              </div>
              {data.leadsBySource.map((s) => {
                const pct = data.totalLeads.value > 0 ? (s.count / data.totalLeads.value) * 100 : 0;
                return (
                  <div key={s.sourceName} className="grid h-7.5 grid-cols-[120px_1fr_44px_64px] items-center gap-3.5 text-[13px]">
                    <span className="truncate font-semibold text-ink">{s.sourceName}</span>
                    <div className="flex h-2 rounded-full bg-divider">
                      <div className="rounded-full bg-primary" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                    <span className="text-right font-[650] tabular-nums text-ink">{pct.toFixed(0)}%</span>
                    <span className="text-right tabular-nums text-text-secondary">{s.count}</span>
                  </div>
                );
              })}
            </div>
          </Card>
          )}
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          {show('attention') && <NeedsAttentionCard data={data} />}

          {show('activity') && (
          <Card className="flex shrink-0 flex-col gap-1.5 py-3 px-4">
            <div className="mb-1 flex h-5 items-center justify-between">
              <h3 className="text-[15px] font-semibold text-ink">Recent activity</h3>
              <Link to="/activities" className="text-[13px] font-semibold text-primary-text hover:underline">
                View all
              </Link>
            </div>
            {data.recentActivity.length === 0 && <p className="text-sm text-text-muted">Nothing yet.</p>}
            {data.recentActivity.map((a) => (
              <div key={a.id} className="flex h-5.5 items-center gap-2 text-[13px]">
                <ActivityIcon kind={a.kind} />
                <span className="min-w-0 flex-1 truncate text-text-secondary">
                  <strong className="font-semibold text-ink">{a.subject}</strong> {a.description}
                </span>
                <span className="shrink-0 text-xs text-text-muted">{formatActivityTime(a.occurredAt)}</span>
              </div>
            ))}
          </Card>
          )}

          {show('team') && (
          <Card className="flex min-h-0 flex-1 flex-col gap-1.5 py-3 px-4">
            <div className="mb-1 flex h-5 items-center justify-between">
              <h3 className="text-[15px] font-semibold text-ink">Team performance</h3>
              <span className="text-xs text-text-muted">Last {days} days</span>
            </div>
            <div role="table" aria-label="Team performance" className="flex flex-col overflow-y-auto">
              <div role="row" className="grid h-5.5 grid-cols-[1fr_36px_60px_68px] items-center gap-2 border-b border-divider text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
                <span role="columnheader">Rep</span>
                <span role="columnheader" className="text-right">Won</span>
                <span role="columnheader" className="text-right">Value</span>
                <span role="columnheader" className="text-right">Activities</span>
              </div>
              {data.teamPerformance.map((r) => (
                <div key={r.membershipId} role="row" className="grid h-6.5 grid-cols-[1fr_36px_60px_68px] items-center gap-2 text-[13px] tabular-nums">
                  <span role="cell" className="flex items-center gap-2 truncate font-semibold text-ink">
                    <Avatar name={r.displayName} size="sm" />
                    {r.displayName}
                  </span>
                  <span role="cell" className="text-right font-semibold">{r.wonCount}</span>
                  <span role="cell" className="text-right">{moneyCompact(r.wonValue)}</span>
                  <span role="cell" className="text-right text-text-secondary">{r.activityCount}</span>
                </div>
              ))}
              {data.teamPerformance.length === 0 && <p className="py-2 text-sm text-text-muted">No closed opportunities in this window yet.</p>}
            </div>
          </Card>
          )}
        </div>
      </div>
    </>
  );
}

function NeedsAttentionCard({ data }: { data: DashboardResponse }) {
  const overdue = data.needsAttention.filter((t) => t.isOverdue);
  const upcoming = data.needsAttention.filter((t) => !t.isOverdue);

  return (
    <Card className="flex shrink-0 flex-col py-3 px-4">
      <div className="mb-2 flex h-5 items-center justify-between">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold text-ink">
          Needs your attention
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber px-1.5 text-xs font-[650] text-night">
            {data.tasksDueToday + data.tasksOverdue}
          </span>
        </h3>
        <Link to="/tasks" className="text-[13px] font-semibold text-primary-text hover:underline">
          All tasks
        </Link>
      </div>

      {overdue.length > 0 && (
        <>
          <span className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-danger">Overdue · {data.tasksOverdue}</span>
          {overdue.map((t) => (
            <div key={t.id} className="flex h-5.5 items-center gap-2 text-[13px]">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B42318" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-ink">{t.title}</span>
                {t.relatedName && <span className="text-text-muted"> · {t.relatedName}</span>}
              </span>
              <span className="shrink-0 text-xs font-semibold text-danger">{daysLate(t.dueAt!)}</span>
            </div>
          ))}
        </>
      )}

      {overdue.length > 0 && upcoming.length > 0 && <div className="my-2 h-px bg-divider" />}

      {upcoming.length > 0 && (
        <>
          <span className="mb-0.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
            Follow-ups today · {data.tasksDueToday}
            <Link to="/tasks" className="text-xs font-semibold normal-case tracking-normal text-primary-text hover:underline">
              View all
            </Link>
          </span>
          {upcoming.map((t) => (
            <div key={t.id} className="flex h-7 items-center gap-2 text-[13px]">
              <span className="w-13.5 shrink-0 text-xs font-semibold tabular-nums text-text-secondary">{formatTime(t.dueAt!)}</span>
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-ink">{t.title}</span>
                {t.relatedName && <span className="text-text-muted"> · {t.relatedName}</span>}
              </span>
              <Link to="/activities" className="flex shrink-0 items-center gap-1 rounded-control border border-border-strong px-2 py-1 text-xs font-semibold text-text-secondary hover:bg-surface-subtle">
                Log call
              </Link>
            </div>
          ))}
        </>
      )}

      {data.needsAttention.length === 0 && <p className="text-sm text-text-muted">Nothing needs attention right now.</p>}
    </Card>
  );
}

function Kpi({ label, value, children }: { label: string; value: React.ReactNode; children?: React.ReactNode }) {
  return (
    <Card className="flex flex-col gap-1 py-3 px-4">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">{label}</span>
      <span className="text-[28px] leading-8 font-[650] tabular-nums tracking-[-0.02em] text-ink">{value}</span>
      {children}
    </Card>
  );
}

function ForecastStat({ label, value, bordered }: { label: string; value: string; bordered?: boolean }) {
  return (
    <div className={`flex flex-col gap-0.5 px-3.5 py-2.5 ${bordered ? 'border-l border-divider' : ''}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">{label}</span>
      <span className="text-[15px] font-[650] tabular-nums text-ink">{value}</span>
    </div>
  );
}

function DeltaLine({ metric, mode, suffix, days }: { metric: DeltaMetric | MoneyDeltaMetric; mode: 'pct' | 'count' | 'pts'; suffix?: string; days: number }) {
  const delta = Number(metric.delta);
  const deltaPct = metric.deltaPct;
  if (delta === 0 && deltaPct === null) return null;
  const up = delta >= 0;
  const label = mode === 'pct' ? `${deltaPct !== null ? Math.abs(deltaPct) : Math.abs(delta)}%` : mode === 'pts' ? `${Math.abs(delta)} pts` : `${Math.abs(delta)}`;
  return (
    <span className={`text-xs font-semibold ${up ? 'text-success' : 'text-danger'}`}>
      {up ? '▲' : '▼'} {label} <span className="font-medium text-text-secondary">{suffix ?? `vs prior ${days} days`}</span>
    </span>
  );
}

function ActivityIcon({ kind }: { kind: string }) {
  const byKind: Record<string, { bg: string; fg: string; path: React.ReactNode }> = {
    note: { bg: '#E3F5F3', fg: '#0F6B63', path: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /> },
    created: { bg: '#ECEFFD', fg: '#2E47D6', path: <path d="M12 5v14M5 12h14" /> },
    assigned: { bg: '#F1ECFD', fg: '#5B34B0', path: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" /></> },
    converted: { bg: '#F1ECFD', fg: '#5B34B0', path: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" /></> },
    stage_changed: { bg: '#E6F4EC', fg: '#1E7A4C', path: <path d="m5 12 5 5 9-10" /> },
    won: { bg: '#E6F4EC', fg: '#1E7A4C', path: <path d="m5 12 5 5 9-10" /> },
    lost: { bg: '#FBE9E7', fg: '#B42318', path: <path d="M18 6 6 18M6 6l12 12" /> },
  };
  const style = byKind[kind] ?? { bg: '#EFEEEA', fg: '#5E6470', path: <circle cx="12" cy="12" r="9" /> };
  return (
    <span className="flex size-4.5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: style.bg }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={style.fg} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {style.path}
      </svg>
    </span>
  );
}

function formatActivityTime(iso: string): string {
  const date = new Date(iso);
  const isToday = date.toDateString() === new Date().toDateString();
  return isToday ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : date.toLocaleDateString('en-US', { weekday: 'short' });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function daysLate(iso: string): string {
  const days = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000));
  return `${days}d late`;
}

/** Pipeline-by-stage bars go from a pale blue at the earliest stage to the
 * full `primary` blue at the latest one (design/orion-crm-design/rendered-
 * html/Dashboard.html's 4 sample stages: #AEBBF2 → #7C8FEA → #4B63DE →
 * #2E47D6), rather than one flat color for every stage. */
function stageBarColor(index: number, total: number): string {
  const light: [number, number, number] = [174, 187, 242];
  const dark: [number, number, number] = [46, 71, 214];
  const t = total > 1 ? index / (total - 1) : 1;
  const [r, g, b] = light.map((c, i) => Math.round(c + (dark[i]! - c) * t));
  return `rgb(${r}, ${g}, ${b})`;
}

function money(value: string): string {
  const n = Number(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

/** `$212.4k` / `$1.28M` style compact currency, matching the design's KPI
 * card treatment (full `money()` elsewhere, e.g. the pipeline-by-stage
 * rows, where the design itself doesn't abbreviate). */
function moneyCompact(value: string): string {
  const n = Number(value);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return money(value);
}
